import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { SessionTimerProvider } from './contexts/SessionTimerContext';
import EmailVerification from './components/pages/auth/EmailVerification';
import TermsAndConditions from './components/pages/auth/TermsAndConditions';
import OtpVerification from './components/pages/auth/OtpVerification';
import ApplicationForm from './components/pages/form/ApplicationForm';
import ApplicationFormPage1 from './components/pages/form/ApplicationFormPage1';
import ApplicationFormPage2 from './components/pages/form/ApplicationFormPage2';
import ApplicationFormPage3 from './components/pages/form/ApplicationFormPage3';
import ApplicationFormPageA from './components/pages/form/ApplicationFormPageA';
import ApplicationFormPage4 from './components/pages/form/ApplicationFormPage4';
import ApplicationFormPage14 from './components/pages/form/ApplicationFormPage14';
import ReferenceFormPage from './components/pages/form/ReferenceFormPage';
import ThankYouPage from './components/pages/form/ThankYouPage';
import NextStepsPage from './components/pages/form/NextStepsPage';
import AdminLogin from './components/pages/admin/AdminLogin';
import SubAdminLogin from './components/pages/admin/SubAdminLogin';
import AdminDashboard from './components/pages/admin/AdminDashboard';
import AdminApplicationEdit from './components/pages/admin/AdminApplicationEdit';
import TestSmsPage from './components/pages/admin/TestSmsPage'; // Import the new test page
import TestRazorpayPage from './components/pages/admin/TestRazorpayPage'; // Import Razorpay test page
import AdminFileRoutes from './components/pages/admin/AdminFileRoutes'; // Import admin file management routes
import UserDashboard from './components/pages/user/UserDashboard';
import ProtectedRoute from './components/common/ProtectedRoute';
import SessionRecorderWrapper from './components/common/SessionRecorderWrapper';
import VisitorLiveRecorder from './components/common/VisitorLiveRecorder';
import ApplicationView from './components/pages/ApplicationView'; // Added for viewing single application
import PaymentDetailsPage from './components/pages/PaymentDetailsPage';
import AreaStretchInfo from './components/pages/AreaStretchInfo';
import DocumentUploadPage from './components/pages/DocumentUploadPage';
import MaintenancePage from './components/pages/MaintenancePage';
import DeadlineClosedOverlay from './components/pages/DeadlineClosedOverlay';

// Initialize Sentry for error tracking
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || 'https://7c56da72b61fea751f9285faa18d1508@o4510740884357120.ingest.de.sentry.io/4510740949631056';
Sentry.init({
  dsn: SENTRY_DSN,
  environment: process.env.NODE_ENV || 'development',
  sendDefaultPii: true,
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  beforeSend(event) {
    // Strip circular DOM references that crash JSON serialization (e.g. HTMLVideoElement from webcam)
    try {
      JSON.stringify(event);
    } catch {
      // If the event itself has circular refs, sanitize breadcrumbs & extra
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(b => {
          try { JSON.stringify(b); return b; } catch { return { ...b, data: { note: 'Circular reference removed' } }; }
        });
      }
      if (event.extra) {
        const cleanExtra: Record<string, unknown> = {};
        for (const key of Object.keys(event.extra)) {
          try { JSON.stringify(event.extra[key]); cleanExtra[key] = event.extra[key]; } catch { cleanExtra[key] = '[Circular reference removed]'; }
        }
        event.extra = cleanExtra;
      }
    }
    return event;
  },
});

const RAW_API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://backend.mngldodo.site' : 'http://localhost:5000');
// Handle both relative (/api) and absolute URLs - strip /api suffix if present for building paths
const API_URL = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;

// Visitor tracking component - tracks page visits
const VisitorTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    const trackVisit = async () => {
      try {
        await fetch(`${API_URL}/api/track-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            path: location.pathname,
            referer: document.referrer || null
          })
        });
      } catch (err) {
        // Silent fail
      }
    };
    trackVisit();
  }, [location.pathname]);
  
  return <>{children}</>;
};

// Maintenance mode wrapper - checks if site is in maintenance mode
const MaintenanceWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(null);
  const [applicationsClosed, setApplicationsClosed] = useState<boolean>(false);
  const [applicationDeadline, setApplicationDeadline] = useState<string | null>(null);
  const location = useLocation();

  // Admin routes should bypass maintenance mode
  const isAdminPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/su-admin');
  // Logged-in admin/supervisor sessions bypass closure overlays on ALL routes
  // (e.g. viewing/editing an application via the user form pages). This is
  // cosmetic only — user login/OTP stays blocked server-side, so faking these
  // localStorage flags cannot be abused to actually apply.
  const isAdminSession =
    localStorage.getItem('adminAuth') === 'true' ||
    localStorage.getItem('subAdminAuth') === 'true';
  const isAdminRoute = isAdminPath || isAdminSession;
  const isMaintenancePage = location.pathname === '/maintenance';

  // Grace period: authenticated users on form/dashboard pages should NOT be interrupted
  const isUserFormRoute = location.pathname.startsWith('/application') ||
    location.pathname.startsWith('/user-dashboard') ||
    location.pathname.startsWith('/otp-verification') ||
    location.pathname.startsWith('/thank-you') ||
    location.pathname.startsWith('/next-steps') ||
    location.pathname.startsWith('/reference-form');

  useEffect(() => {
    const checkMaintenance = async () => {
      // Skip check for admin routes / logged-in admin sessions
      if (isAdminRoute) {
        setIsMaintenanceMode(false);
        setApplicationsClosed(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/maintenance-status`);
        const data = await response.json();
        const inMaintenance = data.maintenanceMode === true;
        const closed = data.applicationsClosed === true || data.deadlinePassed === true;
        setApplicationDeadline(data.applicationDeadline || null);

        // Grace period: if user is already on a form page and has a token, let them continue
        if ((inMaintenance || closed) && isUserFormRoute) {
          const hasToken = document.cookie.split(';').some(c => c.trim().startsWith('token='));
          if (hasToken) {
            setIsMaintenanceMode(false);
            setApplicationsClosed(false);
            return;
          }
        }

        setIsMaintenanceMode(inMaintenance);
        setApplicationsClosed(closed);
      } catch (err) {
        // If we can't check, assume not in maintenance
        setIsMaintenanceMode(false);
      }
    };
    checkMaintenance();
  }, [location.pathname, isAdminRoute, isUserFormRoute]);

  // Show loading while checking maintenance status
  if (isMaintenanceMode === null && !isAdminRoute) {
    return null;
  }

  // Two independent closure modes (admin + maintenance routes always bypass):
  // - maintenanceMode → full "Under Maintenance" page (takes priority if both are on)
  // - applicationsClosed / deadline passed → original page with grey "Applications Closed" overlay
  if (isMaintenanceMode && !isAdminRoute && !isMaintenancePage) {
    return <MaintenancePage />;
  }
  if (applicationsClosed && !isAdminRoute && !isMaintenancePage) {
    return (
      <>
        {children}
        <DeadlineClosedOverlay deadline={applicationDeadline} />
      </>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const location = useLocation();
  // Session timeout state - fetched from backend settings (default 360 minutes = 6 hours)
  const [sessionTimeout, setSessionTimeout] = useState<number>(360);
  const [sessionTimeoutLoaded, setSessionTimeoutLoaded] = useState<boolean>(false);

  // Fetch session timeout from backend settings on app load
  useEffect(() => {
    const fetchSessionTimeout = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.sessionTimeoutMinutes === 'number' && settings.sessionTimeoutMinutes > 0) {
            setSessionTimeout(settings.sessionTimeoutMinutes);
          }
          // Inject Google Analytics gtag.js if measurement ID is configured
          const gaId = settings?.googleAnalyticsMeasurementId;
          if (gaId && typeof gaId === 'string' && gaId.trim() && !document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId.trim()}`;
            document.head.appendChild(script);
            const inlineScript = document.createElement('script');
            inlineScript.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId.trim()}',{send_page_view:true});`;
            document.head.appendChild(inlineScript);
          }
        }
      } catch (error) {
        console.error('Error fetching session timeout setting:', error);
        // Keep default of 360 minutes (6 hours)
      } finally {
        setSessionTimeoutLoaded(true);
      }
    };
    fetchSessionTimeout();
  }, []);

  // Track page views on route change via Google Analytics
  useEffect(() => {
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);

  // Don't render until session timeout is loaded to avoid flash of wrong timer
  if (!sessionTimeoutLoaded) {
    return null;
  }

  const isAdminPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/su-admin');

  return (
    <div className="app">
      <VisitorTracker>
        {!isAdminPath && <VisitorLiveRecorder />}
        <MaintenanceWrapper>
          <UserProfileProvider>
            <SessionTimerProvider sessionDuration={sessionTimeout} warningTime={5}>
              <Routes>
                {/* Maintenance page route */}
                <Route path="/maintenance" element={<MaintenancePage />} />
                
                {/* Public routes */}
                <Route path="/" element={<EmailVerification />} />
                <Route path="/verify-otp" element={<OtpVerification />} />
                <Route path="/area-stretch-info" element={<AreaStretchInfo />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/document-upload/:token" element={<DocumentUploadPage />} />
                
                {/* Protected user routes - requires email verification and OTP */}
                <Route element={<ProtectedRoute redirectPath="/" isAdminRoute={false} />}>
                  <Route path="/user-dashboard" element={<SessionRecorderWrapper><UserDashboard /></SessionRecorderWrapper>} />
                  <Route path="/application-form" element={<SessionRecorderWrapper><ApplicationForm /></SessionRecorderWrapper>} />
                  <Route path="/form/page1" element={<SessionRecorderWrapper><ApplicationFormPage1 /></SessionRecorderWrapper>} />
                  <Route path="/form/page2" element={<SessionRecorderWrapper><ApplicationFormPage2 /></SessionRecorderWrapper>} />
                  <Route path="/form/page3" element={<SessionRecorderWrapper><ApplicationFormPage3 /></SessionRecorderWrapper>} />
                  <Route path="/form/pageA" element={<SessionRecorderWrapper><ApplicationFormPageA /></SessionRecorderWrapper>} />
                  <Route path="/form/page4" element={<SessionRecorderWrapper><ApplicationFormPage4 /></SessionRecorderWrapper>} />
                  <Route path="/form/page14" element={<SessionRecorderWrapper><ApplicationFormPage14 /></SessionRecorderWrapper>} />
                  <Route path="/form/reference-template" element={<SessionRecorderWrapper><ReferenceFormPage /></SessionRecorderWrapper>} />
                  <Route path="/application-view/:applicationId" element={<ApplicationView />} />
                  <Route path="/thank-you/:applicationId" element={<ThankYouPage />} />
                  <Route path="/next-steps/:applicationId" element={<NextStepsPage />} />
                  <Route path="/payment-details/:applicationId" element={<SessionRecorderWrapper><PaymentDetailsPage /></SessionRecorderWrapper>} />
                </Route>
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/su-admin/login" element={<SubAdminLogin />} />
                <Route element={<ProtectedRoute redirectPath="/admin/login" isAdminRoute={true} />}>
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/applications/:id/edit" element={<AdminApplicationEdit />} />
                  <Route path="/admin/test-sms" element={<TestSmsPage />} />
                  <Route path="/admin/test-razorpay" element={<TestRazorpayPage />} />
                  <Route path="/admin/files/*" element={<AdminFileRoutes />} />
                </Route>
              </Routes>
            </SessionTimerProvider>
          </UserProfileProvider>
        </MaintenanceWrapper>
      </VisitorTracker>
    </div>
  );
};

export default App;
